import React, { useContext, useRef, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Form, Row, Col, Typography, Input, theme, DatePicker, Select, Spin, Grid, Tooltip } from 'antd';
import _ from 'lodash';
import dayjs from 'dayjs';

import { MessageContext } from '../../../layouts/NavigationLayout';
import Countries from '../../../constants/Countries';
import CommonEnum from '../../../constants/CommonEnum';

//Helpers
import GeneralHelper from '../../../helpers/general';

// Components
import ButtonGroupComponent from './ButtonGroupComponent';

const { Title, Text } = Typography;
const { Item } = Form;
const { useToken } = theme;
const { useBreakpoint } = Grid;

const PersonalParticularsView = (props) => {

	const [nextDisabled, setNextDisabled] = useState(true);
	const [draftDisabled, setDraftDisabled] = useState(true);
	const [isNextStep, setIsNextStep] = useState(false);
	const [warningPhone, setWarningPhone] = useState(false);
	const [isInitialLoad, setIsInitialLoad] = useState(true);

	const history = useHistory();
	const formRef = useRef(null);
	const { token } = useToken();
	const messageApi = useContext(MessageContext);
	const screens = useBreakpoint();

	const query = GeneralHelper.getParamsFromUrl();

	const { application_id: applicationId } = query || {};
	const { colorPrimary } = token;

	const { getApplication, getPersonalParticularByApplicationId, personalParticularPageProps, onPersonalParticularSubmit, resetPersonalParticularMeta,
		resetPersonalParticularPage, reviewAndSubmissionPageProps, setHasUnsavedChanges, educationBackgroundPageProps, resetEducationBackgroundMeta } = props;
	const { personalInfo, loading, loadingSubmit, meta } = personalParticularPageProps;
	const { application } = reviewAndSubmissionPageProps;
	const { meta: educationBgMeta } = educationBackgroundPageProps;
	const currentStep = _.get(application, 'current_step');

	const { last_name, first_name, preferred_name, chinese_name, gender, birth_date, birth_place,
		require_visa, email, phone, passport_num, country_issue, expiry_date,
		building, street, country, personal_url } = personalInfo || {};

	useEffect(() => {
		if (applicationId) {
			getPersonalParticularByApplicationId(applicationId);
		} else {
			history.push('/application?step=0');	//	return to step 0 if applicationId not exist
		}
		return () => {
			resetPersonalParticularPage();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!_.isNil(personalInfo)) {
			setIsInitialLoad(true);
			checkFormValues();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [personalInfo]);

	useEffect(() => {
		checkFormValues();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isInitialLoad]);

	useEffect(() => {
		if (!_.isNil(meta)) {
			if (meta.code === 200 && isNextStep) {
				if (currentStep === 6) {
					window.location.href = process.env.NODE_ENV === 'development'
						? `/application?step=6&application_id=${applicationId}`
						: `/summercamp/app/application?step=6&application_id=${applicationId}`;
				} else {
					history.push(`/application?step=2&application_id=${applicationId}`);
				}
			} else if (meta.code === 200 && !isNextStep) {
				messageApi.open({
					type: 'success',
					content: `${meta.message}`,
					duration: 2.5
				});
				!nextDisabled && query.application_id && getApplication(query.application_id);
			} else {
				messageApi.open({
					type: 'error',
					content: `${meta.message}`,
					duration: 2.5
				});
			}
		}
		return () => {
			resetPersonalParticularMeta();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [meta]);

	useEffect(() => {
		if (educationBgMeta) {
			if (educationBgMeta.code === 200) {
				messageApi.open({
					type: 'success',
					content: `${educationBgMeta.message}`,
					duration: 2.5
				});
			} else {
				messageApi.open({
					type: 'error',
					content: `${educationBgMeta.message}`,
					duration: 2.5
				});
			}
		}
		return () => {
			resetEducationBackgroundMeta();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [educationBgMeta]);

	const filterOption = (input, option) =>
		(option?.label ?? '').toLowerCase().includes(input.toLowerCase());

	const renderOptionLabel = (label) => {
		if (screens.xs && label.length > 25) {
			return (
				<Tooltip
					autoAdjustOverflow={true}
					placement="top"
					title={label}
					key={label}
				>
					{GeneralHelper.truncateLongText(label, 25)}
				</Tooltip>
			);
		}
		return label;
	};

	const validateEmail = (_, value) => {
		if (value && !/^[\w.%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/i.test(value)) {
			setDraftDisabled(true);
			setHasUnsavedChanges(false);
			setNextDisabled(true);
			return Promise.reject('Please enter a valid email address');
		}
		return Promise.resolve();
	};

	const onPhoneNumberPaste = (event, type) => {
		const pastedText = event.clipboardData.getData('text');
		if (!/[0-9\s()+-]/.test(pastedText)) {
			event.preventDefault();
			if (type === 'first') {
				setWarningPhone(true);
			}
			if (type === 'second') {
				setWarningPhone(false);
			}
		} else {
			setWarningPhone(false);
		}
	};

	const checkFormValues = () => {
		if (formRef.current) {
			if (
				formRef.current.getFieldValue('last_name') &&
				formRef.current.getFieldValue('first_name') &&
				formRef.current.getFieldValue('preferred_name') &&
				formRef.current.getFieldValue('gender') &&
				(formRef.current.getFieldValue('birth_date') || !_.isNil(formRef.current.getFieldValue('birth_date'))) &&
				formRef.current.getFieldValue('birth_place') &&
				formRef.current.getFieldValue('require_visa') &&
				formRef.current.getFieldValue('email') &&
				formRef.current.getFieldValue('phone') &&
				formRef.current.getFieldValue('building') &&
				formRef.current.getFieldValue('street') &&
				formRef.current.getFieldValue('country')
			) {
				setNextDisabled(false);
			} else {
				setNextDisabled(true);
			}

			//Separate trappings for draftDisabled
			if (
				formRef.current.getFieldValue('last_name') ||
				formRef.current.getFieldValue('first_name') ||
				formRef.current.getFieldValue('preferred_name') ||
				formRef.current.getFieldValue('gender') ||
				(formRef.current.getFieldValue('birth_date') || !_.isNil(formRef.current.getFieldValue('birth_date'))) ||
				formRef.current.getFieldValue('birth_place') ||
				formRef.current.getFieldValue('require_visa') ||
				formRef.current.getFieldValue('email') ||
				formRef.current.getFieldValue('phone') ||
				formRef.current.getFieldValue('building') ||
				formRef.current.getFieldValue('street') ||
				formRef.current.getFieldValue('country') ||
				formRef.current.getFieldValue('chinese_name') ||
				formRef.current.getFieldValue('passport_num') ||
				formRef.current.getFieldValue('country_issue') ||
				formRef.current.getFieldValue('expiry_date') ||
				formRef.current.getFieldValue('building') ||
				formRef.current.getFieldValue('personal_url')
			) {
				if (isInitialLoad) {
					setDraftDisabled(true);
					setHasUnsavedChanges(false);
				} else {
					setDraftDisabled(false);
					setHasUnsavedChanges(true);
				}
			} else {
				setDraftDisabled(true);
				setHasUnsavedChanges(false);
			}
		}
	};

	if (loading) {
		return (
			<div
				style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
			>
				<Spin />
			</div>
		);
	}

	return (
		<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
			<Form
				ref={formRef}
				layout="vertical"
				initialValues={{
					last_name,
					first_name,
					preferred_name,
					chinese_name,
					gender,
					birth_date: birth_date ? dayjs(birth_date) : null,
					birth_place,
					require_visa,
					email,
					phone,
					building,
					street,
					country,
					passport_num,
					country_issue,
					expiry_date: expiry_date ? dayjs(expiry_date) : null,
					personal_url
				}}
				onFinish={(values) => {
					const { last_name, first_name, preferred_name, chinese_name, gender, birth_date,
						birth_place, require_visa, email, phone, building, street, country,
						passport_num, country_issue, expiry_date, personal_url } = values;

					const fields = {
						personal_info: {
							application_id: applicationId,
							save_method: nextDisabled ? 'draft_save' : 'final_save',
							last_name,
							first_name,
							preferred_name,
							chinese_name,
							gender,
							birth_date: dayjs(birth_date).startOf('day'),
							birth_place,
							require_visa,
							email,
							phone,
							building,
							street,
							country,
							passport_num,
							country_issue,
							expiry_date: expiry_date ? dayjs(expiry_date).startOf('day') : undefined,
							personal_url
						}
					};

					if (!_.isNil(personalInfo)) {
						onPersonalParticularSubmit({ id: personalInfo._id, fields });
					} else {
						onPersonalParticularSubmit({ fields });
					}
				}}
				style={{ width: '100%' }}
			>
				<Title
					level={2}
					style={{ color: colorPrimary }}
				>
					Step 2
				</Title>
				<Row justify="space-between">
					<Col
						xl={11}
						lg={11}
						md={24}
						sm={24}
						xs={24}
					>
						<div style={{ marginBottom: 20 }}>
							<Text
								strong
								style={{ fontSize: 20 }}
							>
								Personal Particulars
							</Text>
						</div>
						<Item
							label="Family Name / Last Name"
							name="last_name"
							required
						>
							<Input
								placeholder="Enter Family Name / Last Name"
								onChange={(e) => {
									const inputValue = e.target.value;

									// Check if it contains disallowed characters
									if (GeneralHelper.checkDisallowedCharacters(inputValue)) {
										// Sanitize the input by removing disallowed characters
										const sanitizedValue = GeneralHelper.sanitizeInput(inputValue);

										// Update form with sanitized value
										formRef.current.setFieldsValue({ last_name: sanitizedValue });
									}

									// After sanitizing, proceed with your other operations
									GeneralHelper.handleCapitalization(formRef, 'last_name');
									setIsInitialLoad(false);
									checkFormValues();
								}}
								onKeyDown={GeneralHelper.preventDisallowedKeys}
								autoComplete="nope"
							/>
						</Item>
						<Item
							label="Given Name / First Name"
							name="first_name"
							required
						>
							<Input
								placeholder="Enter Given Name / First Name"
								onChange={(e) => {
									const inputValue = e.target.value;

									// Check if it contains disallowed characters
									if (GeneralHelper.checkDisallowedCharacters(inputValue)) {
										// Sanitize the input by removing disallowed characters
										const sanitizedValue = GeneralHelper.sanitizeInput(inputValue);

										// Update form with sanitized value
										formRef.current.setFieldsValue({ first_name: sanitizedValue });
									}

									// After sanitizing, proceed with your other operations
									setIsInitialLoad(false);
									checkFormValues();
								}}
								onKeyDown={GeneralHelper.preventDisallowedKeys}
								autoComplete="nope"
							/>
						</Item>
						<Item
							label="Preferred Name"
							name="preferred_name"
							required
						>
							<Input
								placeholder="Enter Preferred Name"
								onChange={(e) => {
									const inputValue = e.target.value;

									// Check if it contains disallowed characters
									if (GeneralHelper.checkDisallowedCharacters(inputValue)) {
										// Sanitize the input by removing disallowed characters
										const sanitizedValue = GeneralHelper.sanitizeInput(inputValue);

										// Update form with sanitized value
										formRef.current.setFieldsValue({ preferred_name: sanitizedValue });
									}

									// After sanitizing, proceed with your other operations
									setIsInitialLoad(false);
									checkFormValues();
								}}
								onKeyDown={GeneralHelper.preventDisallowedKeys}
								autoComplete="nope"
							/>
						</Item>
						<Item
							label="Name in Chinese (if any)"
							name="chinese_name"
						>
							<Input
								placeholder="Enter Chinese name"
								onChange={(e) => {
									const inputValue = e.target.value;

									// Check if it contains disallowed characters
									if (GeneralHelper.checkDisallowedCharacters(inputValue)) {
										// Sanitize the input by removing disallowed characters
										const sanitizedValue = GeneralHelper.sanitizeInput(inputValue);

										// Update form with sanitized value
										formRef.current.setFieldsValue({ chinese_name: sanitizedValue });
									}

									// After sanitizing, proceed with your other operations
									setIsInitialLoad(false);
									checkFormValues();
								}}
								onKeyDown={GeneralHelper.preventDisallowedKeys}
								autoComplete="nope"
							/>
						</Item>
						<Item
							label="Gender"
							name="gender"
							required
						>
							<Select
								placeholder={'Select Gender'}
								options={CommonEnum.personal_info_gender}
								showSearch
								filterOption={filterOption}
								onChange={() => {
									setIsInitialLoad(false);
									checkFormValues();
								}}
							/>
						</Item>
						<Item
							label="Date of Birth (YYYY-MM)"
							name="birth_date"
							required
						>
							<DatePicker
								picker="month"
								style={{ width: '100%' }}
								disabledDate={current => current && current.year() > 2010}
								defaultPickerValue={dayjs().year(2010)}
								onChange={() => {
									setIsInitialLoad(false);
									checkFormValues();
								}}
							/>
						</Item>
						<Item
							label="Place of Birth"
							name="birth_place"
							required
						>
							<Select
								placeholder={'Select Place of Birth'}
								options={Countries.map((country, index) => ({
									value: country.value,
									label: renderOptionLabel(country.label)
								}))}
								showSearch
								filterOption={filterOption}
								onChange={() => {
									setIsInitialLoad(false);
									checkFormValues();
								}}
							/>
						</Item>
						<Item
							label="Require an entry visa to Hong Kong?"
							name="require_visa"
							required
						>
							<Select
								placeholder="Please select"
								options={[
									{ value: 'yes', label: 'Yes' },
									{ value: 'no', label: 'No' }
								]}
								onChange={() => {
									setIsInitialLoad(false);
									checkFormValues();
								}}
							/>
						</Item>

						<div style={{ marginBottom: 20 }}>
							<Text
								strong
								style={{ fontSize: 20 }}
							>
								Identity Document
							</Text>
						</div>
						<Item
							label="Passport / Exit-Entry Permit (EEP) Number (first 5 numbers including letters)"
							name="passport_num"
						>
							<Input
								placeholder="Enter Passport / EEP Number"
								maxLength={5}
								onChange={(e) => {
									const inputValue = e.target.value;

									// Check if it contains disallowed characters
									if (GeneralHelper.checkDisallowedCharacters(inputValue)) {
										// Sanitize the input by removing disallowed characters
										const sanitizedValue = GeneralHelper.sanitizeInput(inputValue);

										// Update form with sanitized value
										formRef.current.setFieldsValue({ passport_num: sanitizedValue });
									}

									// After sanitizing, proceed with your other operations
									setIsInitialLoad(false);
									checkFormValues();
								}}
								onKeyDown={GeneralHelper.preventDisallowedKeys}
								autoComplete="nope"
							/>
						</Item>
						<Item
							label="Country of Issue"
							name="country_issue"
						>
							<Select
								placeholder={'Select Country of Issue'}
								options={Countries.map((country, index) => ({
									value: country.value,
									label: renderOptionLabel(country.label)
								}))}
								showSearch
								filterOption={filterOption}
								allowClear
								onChange={() => {
									setIsInitialLoad(false);
									checkFormValues();
								}}
								onClear={() => {
									setIsInitialLoad(false);
									checkFormValues();
								}}
							/>
						</Item>
						<Item
							label="Date of Expiry"
							name="expiry_date"
						>
							<DatePicker
								placeholder="Select Date"
								onChange={() => {
									setIsInitialLoad(false);
									checkFormValues();
								}}
								format="YYYY-MM-DD"
								style={{ width: '100%' }}
							/>
						</Item>
					</Col>
					<Col
						xl={11}
						lg={11}
						md={24}
						sm={24}
						xs={24}
					>
						<div style={{ marginBottom: 20 }}>
							<Text
								strong
								style={{ fontSize: 20 }}
							>
								Contact Information
							</Text>
						</div>
						<Item
							label="Institutional Email Address"
							name="email"
							required
							rules={[
								{ validator: validateEmail }
							]}
						>
							<Input
								placeholder="Enter Email Address"
								onChange={() => {
									setIsInitialLoad(false);
									checkFormValues();
								}}
								autoComplete="nope"
							/>
						</Item>
						<Item
							label="Contact No."
							name="phone"
							required
						>
							<Input
								placeholder="Enter Country/region code + area code + local phone no."
								onKeyPress={(event) => {
									if (!/[0-9\s()+-]/.test(event.key)) {
										event.preventDefault();
										setWarningPhone(true);
									} else {
										setWarningPhone(false);
									}
								}}
								onPaste={(e) => onPhoneNumberPaste(e, 'first')}
								onChange={() => {
									setIsInitialLoad(false);
									checkFormValues();
								}}
								autoComplete="nope"
							/>
						</Item>
						{
							warningPhone &&
							<Row style={{ marginTop: -20 }}>
								<Text type="warning">
									Invalid characters. Only digits, dashes, plus symbols, and parentheses are allowed.
								</Text>
							</Row>
						}

						<div style={{ marginBottom: 15 }}>
							<Text
								strong
								style={{ fontSize: 16 }}
							>
								Correspondence Address
							</Text>
						</div>
						<Item
							label="Flat / Room, Floor Name or Bldg."
							name="building"
							required
						>
							<Input
								placeholder="Enter Flat / Room, Floor Name or Bldg."
								onChange={(e) => {
									const inputValue = e.target.value;

									// Check if it contains disallowed characters
									if (GeneralHelper.checkDisallowedCharacters(inputValue)) {
										// Sanitize the input by removing disallowed characters
										const sanitizedValue = GeneralHelper.sanitizeInput(inputValue);

										// Update form with sanitized value
										formRef.current.setFieldsValue({ building: sanitizedValue });
									}

									// After sanitizing, proceed with your other operations
									setIsInitialLoad(false);
									checkFormValues();
								}}
								onKeyDown={GeneralHelper.preventDisallowedKeys}
								autoComplete="nope"
							/>
						</Item>
						<Item
							label="No. and Street Name"
							name="street"
							required
						>
							<Input
								placeholder="Enter No. and Street Name"
								onChange={(e) => {
									const inputValue = e.target.value;

									// Check if it contains disallowed characters
									if (GeneralHelper.checkDisallowedCharacters(inputValue)) {
										// Sanitize the input by removing disallowed characters
										const sanitizedValue = GeneralHelper.sanitizeInput(inputValue);

										// Update form with sanitized value
										formRef.current.setFieldsValue({ street: sanitizedValue });
									}

									// After sanitizing, proceed with your other operations
									setIsInitialLoad(false);
									checkFormValues();
								}}
								onKeyDown={GeneralHelper.preventDisallowedKeys}
								autoComplete="nope"
							/>
						</Item>
						<Item
							label="Country / Region, ZIP / Postal Code"
							name="country"
							required
						>
							<Input
								placeholder="Enter Country / Region, ZIP / Postal Code"
								onChange={(e) => {
									const inputValue = e.target.value;

									// Check if it contains disallowed characters
									if (GeneralHelper.checkDisallowedCharacters(inputValue)) {
										// Sanitize the input by removing disallowed characters
										const sanitizedValue = GeneralHelper.sanitizeInput(inputValue);

										// Update form with sanitized value
										formRef.current.setFieldsValue({ country: sanitizedValue });
									}

									// After sanitizing, proceed with your other operations
									setIsInitialLoad(false);
									checkFormValues();
								}}
								onKeyDown={GeneralHelper.preventDisallowedKeys}
								autoComplete="nope"
							/>
						</Item>
						<Item
							label="Personal Homepage URL (optional)"
							name="personal_url"
						>
							<Input
								placeholder="Enter Personal Homepage URL"
								onChange={(e) => {
									const inputValue = e.target.value;

									// Check if it contains disallowed characters
									if (GeneralHelper.checkDisallowedCharacters(inputValue)) {
										// Sanitize the input by removing disallowed characters
										const sanitizedValue = GeneralHelper.sanitizeInput(inputValue);

										// Update form with sanitized value
										formRef.current.setFieldsValue({ personal_url: sanitizedValue });
									}

									// After sanitizing, proceed with your other operations
									setIsInitialLoad(false);
									checkFormValues();
								}}
								onKeyDown={GeneralHelper.preventDisallowedKeys}
								autoComplete="nope"
							/>
						</Item>
					</Col>
				</Row>
				<ButtonGroupComponent
					currentStep={currentStep}
					draftDisabled={draftDisabled}
					saveDisabled={nextDisabled}
					loadingSubmit={loadingSubmit}
					onSave={() => setIsNextStep(true)}
					isOnPersonalPage={true}
				/>
			</Form>
		</div>
	);
};

export default PersonalParticularsView;
